/**
 * React Query Client Configuration
 *
 * Configures TanStack Query with optimal settings for the contract management application.
 * Includes default caching, retry logic, and stale time configurations.
 */
import { QueryClient } from '@tanstack/react-query';

/**
 * Default stale times for different data types (in milliseconds)
 */
export const STALE_TIMES = {
  /** Contract list - refetch after 30 seconds */
  CONTRACTS: 30 * 1000,
  /** Single contract detail - refetch after 1 minute */
  CONTRACT_DETAIL: 60 * 1000,
  /** User data - refetch after 5 minutes */
  USER: 5 * 60 * 1000,
  /** Static data (suppliers, categories) - refetch after 10 minutes */
  STATIC: 10 * 60 * 1000,
  /** Analytics data - refetch after 2 minutes */
  ANALYTICS: 2 * 60 * 1000,
} as const;

/**
 * Query key factory for consistent cache key generation
 */
export const queryKeys = {
  /** Contract-related query keys */
  contracts: {
    all: ['contracts'] as const,
    lists: () => [...queryKeys.contracts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.contracts.lists(), filters] as const,
    details: () => [...queryKeys.contracts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contracts.details(), id] as const,
    clauses: (id: string) => [...queryKeys.contracts.all, 'clauses', id] as const,
    comments: (id: string) => [...queryKeys.contracts.all, 'comments', id] as const,
    history: (id: string) => [...queryKeys.contracts.all, 'history', id] as const,
    repository: (filters: Record<string, unknown> = {}) => [...queryKeys.contracts.all, 'repository', filters] as const,
  },
  /** User-related query keys */
  users: {
    all: ['users'] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
    list: () => [...queryKeys.users.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
  },
  /** Supplier-related query keys */
  suppliers: {
    all: ['suppliers'] as const,
    list: () => [...queryKeys.suppliers.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.suppliers.all, 'detail', id] as const,
  },
  /** Analytics-related query keys */
  analytics: {
    all: ['analytics'] as const,
    dashboard: () => [...queryKeys.analytics.all, 'dashboard'] as const,
    contracts: () => [...queryKeys.analytics.all, 'contracts'] as const,
  },
  /** Rule book query keys */
  rules: {
    all: ['rules'] as const,
    list: () => [...queryKeys.rules.all, 'list'] as const,
    history: (id: string) => [...queryKeys.rules.all, 'history', id] as const,
    playbooks: () => [...queryKeys.rules.all, 'playbooks'] as const,
    suggestions: (status: string) => [...queryKeys.rules.all, 'suggestions', status] as const,
    playbook: (id: string) => [...queryKeys.rules.all, 'playbook', id] as const,
  },
  /** Contract groups query keys */
  contractGroups: {
    all: ['contractGroups'] as const,
    list: () => [...queryKeys.contractGroups.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.contractGroups.all, 'detail', id] as const,
  },
} as const;

/**
 * Creates and configures the React Query client
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Default stale time - data is fresh for 30 seconds
        staleTime: STALE_TIMES.CONTRACTS,
        // Cache data for 5 minutes after becoming inactive
        gcTime: 5 * 60 * 1000,
        // Retry failed requests up to 3 times
        retry: 3,
        // Exponential backoff for retries
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus in production only
        refetchOnWindowFocus: process.env.NODE_ENV === 'production',
        // Don't refetch on reconnect in development
        refetchOnReconnect: process.env.NODE_ENV === 'production',
        // Don't refetch on mount if data is fresh
        refetchOnMount: true,
      },
      mutations: {
        // Retry mutations once on failure
        retry: 1,
        // Show errors in development
        onError: (error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Mutation error:', error);
          }
        },
      },
    },
  });
}

/**
 * Singleton query client instance
 */
export const queryClient = createQueryClient();

export default queryClient;
